/**
 * PR-C2 Phase 2 (ADR-0036, механизм B) — канонический хеш КП-производной
 * спецификации договора.
 *
 * Назначение: pristine-детектор для частичного пересева. При первом севе из КП
 * хеш записывается в `ContractDraft.seededSpecHash`. При forward-открытии, если
 * КП изменился, сравниваем `computeSpecHash(state.spec)` с сохранённым:
 *   - совпал → пользователь НЕ трогал спецификацию → можно пересеять целиком;
 *   - не совпал → есть ручные правки → спецификацию не трогаем (решение #4).
 *
 * **Инвариантность (критично — см. ADR-0036 §2a):**
 *   - `row.id` ИСКЛЮЧЁН: UUID регенерируется при каждом севе из КП, иначе хеш
 *     никогда бы не совпал.
 *   - строки `key === 'block_cost'` ИСКЛЮЧЕНЫ: стоимость управляется отдельным
 *     дискриминатором `costSource`, а не хешем спецификации (иначе пересев цены
 *     делал бы спец «грязной» на следующем mount).
 *   - стоимость кастомных строк (`cost` у `custom-*`) ВКЛЮЧЕНА: если пользователь
 *     проставил цену позиции вручную — это ручная правка → спец «грязная».
 *   - порядок блоков и строк ЗНАЧИМ (повторяет порядок КП).
 *
 * Чистая функция (no React, no I/O). Хеш — FNV-1a 32-bit → 8-hex-символьная
 * строка (помещается в `seededSpecHash` max(64)).
 */

import type { ContractSpecBlock } from '@pomoshnik/shared';

/**
 * Каноническая сериализация спецификации, нечувствительная к UUID и к
 * block_cost.cost (см. JSDoc модуля). Детерминированная — ключи в фиксированном
 * порядке, без зависимости от вставки.
 */
function canonicalizeSpec(spec: readonly ContractSpecBlock[]): string {
  const blocks = spec.map((block) => ({
    title: block.title,
    rows: block.rows
      .filter((row) => row.key !== 'block_cost')
      .map((row) => ({
        label: row.label,
        type: row.type ?? '',
        brand: row.brand ?? '',
        cost: row.cost ?? null,
      })),
  }));
  return JSON.stringify(blocks);
}

/**
 * FNV-1a 32-bit хеш строки → 8-символьный hex. Детерминированный, без коллизий
 * для нашего объёма (десятки позиций, single-user). Не криптографический —
 * используется только для equality-сравнения pristine-vs-dirty.
 */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619 в 32-битной арифметике (Math.imul избегает потери точности).
    hash = Math.imul(hash, 0x01000193);
  }
  // >>> 0 → unsigned 32-bit; padStart до стабильной длины.
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Канонический хеш КП-производной спецификации (механизм B, ADR-0036 §2a). */
export function computeSpecHash(spec: readonly ContractSpecBlock[]): string {
  return fnv1a(canonicalizeSpec(spec));
}
